/**
 * APP_CONFIG — central app-identitet for AddonNordic Studio remix-template.
 *
 * Når dette projekt remixes til en ny app, er dette typisk den FØRSTE
 * fil der skal opdateres. Foundation-kode (src/foundation/, supabase/functions/foundation-*)
 * læser herfra i stedet for at hardcode "time" / "NordicTime".
 *
 * Se REMIX.md for fuld procedure.
 */

export const APP_CONFIG = {
  /** Bruges i routes /app/{slug}, /embed/{slug} og som tag i monitoring. */
  slug: "time",
  name: "NordicTime",
  tagline: "Dansk arbejdstidsregistrering — direkte i monday.",
  domain: "nordictime.app",
  parentBrand: "AddonNordic",
  parentDomain: "addonnordic.dk",

  marketplace: {
    manifestPath: "/marketplace/time-manifest.json",
    listingPath: "/marketplace/TIME_LISTING.md",
  },

  paddle: {
    products: {
      starter_monthly: "time_starter_monthly",
      starter_yearly: "time_starter_yearly",
      team_monthly: "time_team_monthly",
      team_yearly: "time_team_yearly",
      business_monthly: "time_business_monthly",
      business_yearly: "time_business_yearly",
    },
    /** Hvilken DB-tabel webhook skriver tier til. */
    tierTable: "time_settings",
  },

  monday: {
    embedRoute: "/embed/time",
    requiredScopes: [
      "boards:read",
      "boards:write",
      "me:read",
      "users:read",
      "webhooks:write",
      "notifications:write",
    ],
    apiVersion: "2026-05",
  },

  features: {
    cvrLookup: true,
    currencyRates: false,
    aiCoach: false,
  },

  monitoring: {
    /** Tag på Sentry/GlitchTip events for at filtrere pr. app. */
    projectTag: "addonnordic-time",
    /** Hvilken provider DSN'en peger på. Påvirker Studio Monitoring-wizard. */
    provider: "glitchtip" as "glitchtip" | "sentry" | "both" | "none",
    /**
     * Fallback DSN brugt hvis VITE_GLITCHTIP_DSN / VITE_SENTRY_DSN ikke er sat.
     * Time har en legacy Sentry-konto bag denne. Nye remix-apps SKAL sætte
     * env-secret i stedet og lade dette felt være `undefined`.
     */
    fallbackDsn:
      "https://14b059a9e4f0b190e0a3ad87b964a75f@o4511235471900672.ingest.de.sentry.io/4511235493462096" as
        | string
        | undefined,
  },

  support: {
    email: "support@nordictime.app",
    /** Phase: 'gmail' (0-50 kunder) → 'crisp' (50-200) → 'freshdesk' (200+). */
    provider: "gmail" as "gmail" | "crisp" | "freshdesk",
  },

  /** AEO/SEO — JSON-LD FAQPage schema for AI-crawlers (ChatGPT/Perplexity/Gemini). */
  aeo: {
    /** Vises på /faq.json pr. app. Redigeres i Studio → AEO. */
    enabled: true,
  },

  /**
   * Pain pages (headless landing pages i `published_pain_pages`).
   * Hver remix filtrerer på sit eget app_slug så Relarks pages ikke lækker
   * ind i NordicTime og omvendt. Vertical styrer constraint i DB.
   */
  painPages: {
    appSlug: "time",
    /** manager | parenting | hr | finance | ops | sales | it | other */
    defaultVertical: "hr" as const,
  },
} as const;

export type AppConfig = typeof APP_CONFIG;
